import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import {
  ChatCircle,
  PaperPlaneRight,
  Robot,
  User,
  MessengerLogo,
  InstagramLogo,
  MagnifyingGlass,
  CheckCircle,
  Clock,
  Tag,
  Note,
  Lightning,
  UserCircle,
  X,
  Plus,
  Trash,
  Phone,
  EnvelopeSimple,
  MapPin,
  CalendarCheck,
  CaretDown,
  ArrowCounterClockwise,
  Image,
  MagnifyingGlassPlus,
  GitMerge,
  Warning,
  Spinner,
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WhatsAppIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function WebchatIcon({ size = 18, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2C6.48 2 2 6.03 2 11c0 2.7 1.22 5.12 3.17 6.8L4 22l4.64-2.02A10.64 10.64 0 0 0 12 20c5.52 0 10-4.03 10-9S17.52 2 12 2z"/>
      <circle cx="8" cy="11" r="1.2" fill="white"/>
      <circle cx="12" cy="11" r="1.2" fill="white"/>
      <circle cx="16" cy="11" r="1.2" fill="white"/>
    </svg>
  );
}

function platformStyle(platform) {
  if (platform === 'messenger') return { Icon: MessengerLogo, bg: 'bg-blue-100', color: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' };
  if (platform === 'whatsapp') return { Icon: WhatsAppIcon, bg: 'bg-green-100', color: 'text-[#25D366]', badge: 'bg-green-100 text-green-700' };
  if (platform === 'webchat')  return { Icon: WebchatIcon,  bg: 'bg-sky-100',   color: 'text-sky-500',   badge: 'bg-sky-100 text-sky-700' };
  return { Icon: InstagramLogo, bg: 'bg-gradient-to-br from-purple-100 to-pink-100', color: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' };
}

const LABEL_COLORS = {
  'booking': 'bg-indigo-100 text-indigo-700',
  'opfølgning': 'bg-amber-100 text-amber-700',
  'klage': 'bg-rose-100 text-rose-700',
  'ny kunde': 'bg-emerald-100 text-emerald-700',
  'vip': 'bg-violet-100 text-violet-700',
};
const ALL_LABELS = Object.keys(LABEL_COLORS);

function ConversationList({ conversations, selectedId, onSelect, filter }) {
  const { t } = useLanguage();
  const filtered = filter === 'all' ? conversations : conversations.filter(c => c.platform === filter);

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Lige nu';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}t`;
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };

  const statusIcons = {
    active: <div className="w-2 h-2 rounded-full bg-emerald-500" />,
    resolved: <CheckCircle size={14} className="text-zinc-400" />,
    pending: <Clock size={14} className="text-amber-500" />,
  };

  return (
    <div className="h-full flex flex-col">
      {filtered.length > 0 ? (
        <ScrollArea className="flex-1">
          {filtered.map((conv) => {
            const pStyle = platformStyle(conv.platform);
            const isSelected = conv.id === selectedId;
            return (
              <div
                key={conv.id}
                onClick={() => onSelect(conv)}
                className={`p-4 border-b border-zinc-100 cursor-pointer transition-colors duration-150 ${
                  isSelected ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-zinc-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${pStyle.bg}`}>
                    <pStyle.Icon size={22} weight={conv.platform === 'whatsapp' ? undefined : 'fill'} className={pStyle.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-zinc-900 truncate text-sm">
                        {conv.user_name || `User ${conv.user_id.slice(-6)}`}
                      </span>
                      <span className="text-[11px] text-zinc-400 flex-shrink-0">{formatTime(conv.last_message_time)}</span>
                    </div>
                    <p className="text-[13px] text-zinc-500 mt-0.5 line-clamp-2 leading-snug">{conv.last_message || 'Ingen beskeder'}</p>
                    {conv.attention_reason && (
                      <p className="text-[11px] font-semibold text-amber-600 mt-0.5">⚠ Kræver opfølgning</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                      {statusIcons[conv.status]}
                      <span className="text-[11px] text-zinc-400 capitalize">{conv.status}</span>
                      <span className="text-[11px] text-zinc-300">·</span>
                      <span className="text-[11px] text-zinc-400">{conv.message_count} beskeder</span>
                      {conv.assigned_to && (
                        <>
                          <span className="text-[11px] text-zinc-300">·</span>
                          <span className="text-[11px] text-zinc-400">{conv.assigned_to}</span>
                        </>
                      )}
                      {conv.labels?.map(l => (
                        <span key={l} className={`text-[10px] px-1.5 py-0 rounded-full ${LABEL_COLORS[l] || 'bg-zinc-100 text-zinc-600'}`}>{l}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </ScrollArea>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <ChatCircle size={48} className="mx-auto mb-3 text-zinc-300" />
            <p>{t('noConversations')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickRepliesMenu({ onSelect, onClose }) {
  const [replies, setReplies] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quick-replies`).then(r => setReplies(r.data)).catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!newTitle.trim() || !newText.trim()) return;
    const r = await axios.post(`${API}/quick-replies`, null, { params: { title: newTitle, text: newText } });
    setReplies(prev => [...prev, r.data]);
    setNewTitle(''); setNewText(''); setAdding(false);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/quick-replies/${id}`);
    setReplies(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div className="absolute bottom-14 left-0 right-0 bg-white border border-zinc-200 rounded-xl shadow-xl z-20" style={{maxHeight: '260px', display: 'flex', flexDirection: 'column'}}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100" style={{flexShrink: 0}}>
        <span className="text-sm font-semibold text-zinc-700">Hurtigsvar</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600"><X size={16} /></button>
      </div>
      <div style={{overflowY: 'auto', flex: 1}}>
        {replies.map(r => (
          <div key={r.id} className="flex items-start gap-2 px-4 py-2.5 hover:bg-zinc-50 group cursor-pointer" onClick={() => { onSelect(r.text); onClose(); }}>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-zinc-700">{r.title}</p>
              <p className="text-sm text-zinc-500 truncate">{r.text}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }} className="opacity-0 group-hover:opacity-100 text-zinc-300 hover:text-rose-500 flex-shrink-0 mt-0.5">
              <Trash size={14} />
            </button>
          </div>
        ))}
        {adding && (
          <div className="px-4 py-3 border-t border-zinc-100 space-y-2">
            <Input placeholder="Titel" value={newTitle} onChange={e => setNewTitle(e.target.value)} className="text-sm h-8" />
            <Input placeholder="Tekst" value={newText} onChange={e => setNewText(e.target.value)} className="text-sm h-8" />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} className="h-7 text-xs bg-blue-600 hover:bg-blue-700">Gem</Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)} className="h-7 text-xs">Annuller</Button>
            </div>
          </div>
        )}
      </div>
      {!adding && (
        <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 px-4 py-2.5 text-xs text-blue-600 hover:bg-blue-50 border-t border-zinc-100" style={{flexShrink: 0}}>
          <Plus size={14} /> Nyt hurtigsvar
        </button>
      )}
    </div>
  );
}

function Lightbox({ url, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white">
        <X size={28} />
      </button>
      <img
        src={url}
        alt="Forstørret billede"
        className="max-w-full max-h-full rounded-lg object-contain"
        style={{maxWidth: '90vw', maxHeight: '90vh'}}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function MergeModal({ onClose, onMerged }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null); // pair key being merged
  const [primaryChoice, setPrimaryChoice] = useState({}); // pair_key -> 'a' | 'b'
  const [done, setDone] = useState({}); // pair_key -> true

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    axios.get(`${API}/conversations/merge-suggestions`)
      .then(r => setSuggestions(r.data))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, []);

  const confidenceColor = (c) => {
    if (c >= 80) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
    if (c >= 55) return { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
    return { bar: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' };
  };

  const formatTime = (t) => {
    if (!t) return '';
    const d = new Date(t);
    return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };

  const handleMerge = async (s) => {
    const pairKey = s.conversation_a.id + '_' + s.conversation_b.id;
    const choice = primaryChoice[pairKey] || 'a';
    const primaryId = choice === 'a' ? s.conversation_a.id : s.conversation_b.id;
    const secondaryId = choice === 'a' ? s.conversation_b.id : s.conversation_a.id;
    setMerging(pairKey);
    try {
      await axios.post(`${API}/conversations/merge`, null, { params: { primary_id: primaryId, secondary_id: secondaryId } });
      setDone(prev => ({ ...prev, [pairKey]: true }));
      onMerged();
    } catch (e) {
      console.error(e);
    } finally {
      setMerging(null);
    }
  };

  const platformIcon = (platform) => {
    if (platform === 'messenger') return <MessengerLogo size={13} weight="fill" className="text-blue-500" />;
    if (platform === 'whatsapp') return <WhatsAppIcon size={13} className="text-[#25D366]" />;
    return <InstagramLogo size={13} weight="fill" className="text-purple-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <GitMerge size={20} className="text-blue-600" />
            <h2 className="font-semibold text-zinc-900">Flet samtaler</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-zinc-400">
              <Spinner size={24} className="animate-spin mr-2" /> Analyserer samtaler...
            </div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              <CheckCircle size={40} className="mx-auto mb-3 text-emerald-400" />
              <p className="font-medium">Ingen dubletter fundet</p>
              <p className="text-sm text-zinc-400 mt-1">Systemet fandt ingen samtaler der ser ud til at høre til samme person.</p>
            </div>
          )}
          {suggestions.map((s) => {
            const pairKey = s.conversation_a.id + '_' + s.conversation_b.id;
            const col = confidenceColor(s.confidence);
            const choice = primaryChoice[pairKey] || 'a';
            const isDone = done[pairKey];
            const isMerging = merging === pairKey;

            if (isDone) {
              return (
                <div key={pairKey} className="border border-zinc-100 rounded-xl p-4 bg-zinc-50 flex items-center gap-2 text-sm text-zinc-400">
                  <CheckCircle size={16} className="text-emerald-500" /> Flettet
                </div>
              );
            }

            return (
              <div key={pairKey} className={`border rounded-xl p-4 ${col.bg}`}>
                {/* Confidence bar */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${col.bar}`} style={{ width: `${s.confidence}%` }} />
                  </div>
                  <span className={`text-sm font-bold ${col.text} flex-shrink-0`}>{s.confidence}% sikker</span>
                </div>

                {/* Two conversation cards */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[['a', s.conversation_a], ['b', s.conversation_b]].map(([key, conv]) => (
                    <button
                      key={key}
                      onClick={() => setPrimaryChoice(prev => ({ ...prev, [pairKey]: key }))}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${
                        choice === key
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-zinc-200 bg-white hover:border-zinc-300'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        {platformIcon(conv.platform)}
                        <span className="text-xs font-semibold text-zinc-700 truncate">{conv.user_name}</span>
                        {choice === key && (
                          <span className="ml-auto text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">Primær</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 truncate mb-1.5">{conv.last_message || 'Ingen besked'}</p>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                        <span>{conv.message_count} beskeder</span>
                        <span>·</span>
                        <span>{formatTime(conv.last_message_time)}</span>
                      </div>
                      {(conv.signals.phones.length > 0 || conv.signals.emails.length > 0 || conv.signals.customer_id) && (
                        <div className="mt-2 pt-2 border-t border-zinc-100 space-y-0.5">
                          {conv.signals.phones.map(p => (
                            <div key={p} className="flex items-center gap-1 text-[10px] text-zinc-500">
                              <Phone size={9} /> {p}
                            </div>
                          ))}
                          {conv.signals.emails.map(e => (
                            <div key={e} className="flex items-center gap-1 text-[10px] text-zinc-500">
                              <EnvelopeSimple size={9} /> {e}
                            </div>
                          ))}
                          {conv.signals.customer_id && (
                            <div className="text-[10px] text-zinc-400">GECKO ID: {conv.signals.customer_id}</div>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <p className="text-xs text-zinc-500 flex-1">
                    Klik på den samtale der skal være primær (beholder alle beskeder fra begge)
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleMerge(s)}
                    disabled={isMerging}
                    className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs px-4 flex-shrink-0"
                  >
                    {isMerging ? <Spinner size={14} className="animate-spin" /> : <><GitMerge size={14} className="mr-1" /> Flet</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ChatThread({ conversation, messages, onSendMessage, onMarkResolved, onReopen, onBotPauseToggle, onRefreshMessages }) {
  const { t } = useLanguage();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'notes'
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [reactions, setReactions] = useState({}); // msgId -> emoji
  const [hoverMsgId, setHoverMsgId] = useState(null);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const botPaused = conversation?.bot_paused || false;

  useEffect(() => {
    // Small delay so DOM has rendered before we scroll
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);
  }, [messages, notes, activeTab]);

  useEffect(() => {
    if (conversation) {
      axios.get(`${API}/conversations/${conversation.id}/notes`).then(r => setNotes(r.data)).catch(() => {});
    }
  }, [conversation]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    await onSendMessage(newMessage);
    setNewMessage('');
    setSending(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const r = await axios.post(`${API}/conversations/${conversation.id}/notes`, null, { params: { content: newNote, author: 'Agent' } });
    setNotes(prev => [...prev, r.data]);
    setNewNote('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); activeTab === 'chat' ? handleSend() : handleAddNote(); }
  };

  const insertEmoji = (emoji) => {
    const input = inputRef.current;
    if (!input) { setNewMessage(prev => prev + emoji); return; }
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const current = activeTab === 'chat' ? newMessage : newNote;
    const updated = current.slice(0, start) + emoji + current.slice(end);
    if (activeTab === 'chat') setNewMessage(updated);
    else setNewNote(updated);
    // Restore cursor position after emoji insert
    setTimeout(() => {
      input.selectionStart = start + emoji.length;
      input.selectionEnd = start + emoji.length;
      input.focus();
    }, 0);
    setShowEmojiPicker(false);
  };

  const COMMON_EMOJIS = [
    '😊','😂','❤️','👍','🙏','😍','🎉','✅','👋','💪',
    '😅','🔥','💯','🙌','😘','💕','🥰','😎','🤩','🎊',
    '⭐','🌸','✨','💅','💆','💇','🧴','💋','🌟','👸',
  ];

  const REACTION_EMOJIS = ['❤️','👍','😂','😮','😢','🙏','🔥','✅'];

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !conversation) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = uploadRes.data.url;
      await axios.post(`${API}/conversations/${conversation.id}/reply-image`, null, { params: { image_url: imageUrl } });
      if (onRefreshMessages) onRefreshMessages();
    } catch (err) {
      console.error('Upload fejl:', err);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleReaction = (msgId, emoji) => {
    setReactions(prev => {
      if (prev[msgId] === emoji) {
        const next = { ...prev };
        delete next[msgId];
        return next;
      }
      return { ...prev, [msgId]: emoji };
    });
    setReactionPickerMsgId(null);
  };

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50">
        <div className="text-center text-zinc-500">
          <ChatCircle size={64} className="mx-auto mb-4 text-zinc-300" />
          <p className="text-lg">{t('selectConversation')}</p>
        </div>
      </div>
    );
  }

  const pStyle = platformStyle(conversation.platform);

  return (
    <div className="h-full flex flex-col">
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-white flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${pStyle.bg}`}>
            <pStyle.Icon size={18} weight={conversation.platform === 'whatsapp' ? undefined : 'fill'} className={pStyle.color} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-zinc-900 truncate text-sm">
              {conversation.user_name || `User ${conversation.user_id.slice(-6)}`}
            </h3>
            <div className="flex items-center gap-1.5">
              <Badge className={`text-xs px-1.5 py-0 ${pStyle.badge}`}>{conversation.platform}</Badge>
              <span className="text-xs text-zinc-400">{conversation.message_count} beskeder</span>
              {conversation.assigned_to && <span className="text-xs text-zinc-400">· {conversation.assigned_to}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Bot pause toggle */}
          <button
            onClick={() => onBotPauseToggle(!botPaused)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              botPaused
                ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
            }`}
            title={botPaused ? 'Giv tilbage til Polly' : 'Overtag samtale'}
          >
            <Robot size={14} className={botPaused ? 'text-amber-500 line-through' : 'text-zinc-400'} />
            {botPaused ? 'Giv til Polly' : 'Overtag'}
          </button>
          {conversation.status !== 'resolved' ? (
            <Button variant="outline" size="sm" onClick={onMarkResolved} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-8 text-xs">
              <CheckCircle size={14} className="mr-1" /> Løst
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onReopen} className="text-amber-600 border-amber-200 hover:bg-amber-50 h-8 text-xs">
              <ArrowCounterClockwise size={14} className="mr-1" /> Genåbn
            </Button>
          )}
        </div>
      </div>

      {/* Bot paused banner */}
      {botPaused && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-xs text-amber-700">
          <Robot size={13} />
          <span>Polly er sat på pause — du skriver direkte til kunden som agent</span>
        </div>
      )}

      {/* Chat / Notes tabs */}
      <div className="flex border-b border-zinc-200 bg-white">
        <button onClick={() => setActiveTab('chat')} className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${activeTab === 'chat' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-zinc-500 hover:text-zinc-700'}`}>
          <ChatCircle size={15} /> Chat
        </button>
        <button onClick={() => setActiveTab('notes')} className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${activeTab === 'notes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-zinc-500 hover:text-zinc-700'}`}>
          <Note size={15} /> Noter {notes.length > 0 && <span className="bg-amber-100 text-amber-700 text-xs rounded-full px-1.5">{notes.length}</span>}
        </button>
      </div>

      {/* Messages or Notes */}
      <ScrollArea className="flex-1 bg-zinc-50">
        {activeTab === 'chat' ? (
          <div className="p-4 space-y-4">
            {messages.map((msg) => {
              const isBot = msg.sender_type === 'bot';
              const reaction = reactions[msg.id];
              const isHovered = hoverMsgId === msg.id;
              const showPicker = reactionPickerMsgId === msg.id;
              return (
                <div
                  key={msg.id}
                  className={`flex gap-2.5 ${isBot ? 'justify-start' : 'justify-end'}`}
                  onMouseEnter={() => setHoverMsgId(msg.id)}
                  onMouseLeave={() => { setHoverMsgId(null); if (!showPicker) setReactionPickerMsgId(null); }}
                >
                  {isBot && (
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Robot size={14} className="text-indigo-600" />
                    </div>
                  )}
                  <div className="relative" style={{ maxWidth: '75%' }}>
                    {/* Reaction picker popup — anchored to top corner */}
                    {showPicker && (
                      <div
                        className={`absolute z-30 top-0 bg-white border border-zinc-200 rounded-full shadow-lg px-2 py-1.5 flex gap-1 ${isBot ? 'left-full ml-2' : 'right-full mr-2'}`}
                        onMouseEnter={() => setHoverMsgId(msg.id)}
                        onMouseLeave={() => { setHoverMsgId(null); setReactionPickerMsgId(null); }}
                      >
                        {REACTION_EMOJIS.map(e => (
                          <button
                            key={e}
                            onClick={() => handleReaction(msg.id, e)}
                            className={`text-2xl hover:scale-125 transition-transform leading-none p-1 rounded-full ${reaction === e ? 'bg-blue-100' : ''}`}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Bubble */}
                    <div
                      className={`${isBot ? 'bg-white border border-indigo-100' : 'bg-white border border-zinc-200'} rounded-xl px-3.5 py-2.5 cursor-default`}
                      onClick={() => setReactionPickerMsgId(showPicker ? null : msg.id)}
                    >
                      {msg.message_type === 'image' && msg.image_url ? (
                        <img
                          src={msg.image_url}
                          alt="billede"
                          className="max-w-full rounded-lg max-h-56 object-contain cursor-zoom-in"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          onLoad={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
                          onDoubleClick={(e) => { e.stopPropagation(); setLightboxUrl(msg.image_url); }}
                          onClick={(e) => e.stopPropagation()}
                          title="Dobbeltklik for at forstørre"
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap text-zinc-800">{msg.content}</p>
                      )}
                      <p className="text-[10px] text-zinc-400 mt-1">{formatTime(msg.timestamp)}</p>
                    </div>

                    {/* Reaction badge — top corner of bubble */}
                    {reaction && (
                      <button
                        onClick={() => handleReaction(msg.id, reaction)}
                        className={`absolute -top-4 ${isBot ? '-right-3' : '-left-3'} bg-white border border-zinc-200 rounded-full text-xl px-1.5 py-0.5 shadow-sm leading-none hover:scale-110 transition-transform`}
                        title="Klik for at fjerne"
                      >
                        {reaction}
                      </button>
                    )}

                    {/* Hover add-reaction hint — top corner */}
                    {isHovered && !reaction && !showPicker && (
                      <button
                        onClick={() => setReactionPickerMsgId(msg.id)}
                        className={`absolute -top-4 ${isBot ? '-right-3' : '-left-3'} bg-white border border-zinc-200 rounded-full text-base px-1.5 py-0.5 shadow-sm text-zinc-400 hover:text-zinc-600 leading-none`}
                        title="Tilføj reaktion"
                      >
                        +😊
                      </button>
                    )}
                  </div>
                  {!isBot && (
                    <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0 mt-1">
                      <User size={14} className="text-zinc-600" />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {notes.length === 0 && (
              <div className="text-center text-zinc-400 py-8">
                <Note size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">Ingen interne noter endnu</p>
              </div>
            )}
            {notes.map((note) => (
              <div key={note.id} className="bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <UserCircle size={14} className="text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">{note.author}</span>
                  <span className="text-[10px] text-amber-500 ml-auto">{formatTime(note.timestamp)}</span>
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{note.content}</p>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t border-zinc-200 bg-white relative">
        {showQuickReplies && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowQuickReplies(false)} />
            <QuickRepliesMenu onSelect={(text) => setNewMessage(text)} onClose={() => setShowQuickReplies(false)} />
          </>
        )}
        {showEmojiPicker && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowEmojiPicker(false)} />
          <div className="absolute bottom-14 left-0 right-0 bg-white border border-zinc-200 rounded-xl shadow-xl z-20 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-zinc-500">Emoji</span>
              <button onClick={() => setShowEmojiPicker(false)} className="text-zinc-400 hover:text-zinc-600"><X size={14} /></button>
            </div>
            <div className="flex flex-wrap gap-1">
              {COMMON_EMOJIS.map(e => (
                <button
                  key={e}
                  onClick={() => insertEmoji(e)}
                  className="text-xl hover:bg-zinc-100 rounded p-1 transition-colors leading-none"
                  title={e}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        <div className="flex gap-2 items-center">
          {activeTab === 'chat' && (
            <button onClick={() => { setShowQuickReplies(v => !v); setShowEmojiPicker(false); }} className="text-zinc-400 hover:text-blue-600 flex-shrink-0" title="Hurtigsvar">
              <Lightning size={20} />
            </button>
          )}
          {activeTab === 'chat' && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="text-zinc-400 hover:text-blue-600 flex-shrink-0"
              title="Send billede"
            >
              {uploadingImage ? <Spinner size={20} className="animate-spin" /> : <Image size={20} />}
            </button>
          )}
          <button
            onClick={() => { setShowEmojiPicker(v => !v); setShowQuickReplies(false); }}
            className="text-zinc-400 hover:text-amber-500 flex-shrink-0 text-lg leading-none"
            title="Emoji"
          >
            😊
          </button>
          <Input
            ref={inputRef}
            value={activeTab === 'chat' ? newMessage : newNote}
            onChange={(e) => activeTab === 'chat' ? setNewMessage(e.target.value) : setNewNote(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={activeTab === 'chat' ? t('typeMessage') : 'Skriv en intern note...'}
            className={`flex-1 ${activeTab === 'notes' ? 'bg-amber-50 border-amber-200 placeholder:text-amber-400' : ''}`}
          />
          <Button
            onClick={activeTab === 'chat' ? handleSend : handleAddNote}
            disabled={activeTab === 'chat' ? (!newMessage.trim() || sending) : !newNote.trim()}
            className={activeTab === 'chat' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-500 hover:bg-amber-600'}
          >
            <PaperPlaneRight size={16} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ContactPanel({ conversation, onLabelChange, onAssign, agents }) {
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const currentLabels = conversation?.labels || [];

  useEffect(() => {
    if (!conversation) return;
    // Try to get customer info from conversation doc
    axios.get(`${API}/conversations/${conversation.id}`).then(r => {
      const conv = r.data.conversation;
      if (conv?.customer_info) setCustomerInfo(conv.customer_info);
    }).catch(() => {});
  }, [conversation?.id]);

  if (!conversation) return null;

  const toggleLabel = (label) => {
    const updated = currentLabels.includes(label)
      ? currentLabels.filter(l => l !== label)
      : [...currentLabels, label];
    onLabelChange(updated);
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-zinc-200 overflow-y-auto">
      {/* Contact info */}
      <div className="p-4 border-b border-zinc-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center">
            <User size={24} className="text-zinc-400" />
          </div>
          <div>
            <p className="font-semibold text-zinc-900 text-sm">{conversation.user_name || 'Ukendt'}</p>
            <p className="text-xs text-zinc-400 capitalize">{conversation.platform}</p>
          </div>
        </div>

        {customerInfo && (
          <div className="space-y-2 mt-3">
            {customerInfo.phone && (
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Phone size={14} className="text-zinc-400 flex-shrink-0" />
                <span>{customerInfo.phone}</span>
              </div>
            )}
            {customerInfo.email && (
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <EnvelopeSimple size={14} className="text-zinc-400 flex-shrink-0" />
                <span className="truncate">{customerInfo.email}</span>
              </div>
            )}
            {(customerInfo.city || customerInfo.clinic_city) && (
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <MapPin size={14} className="text-zinc-400 flex-shrink-0" />
                <span>{customerInfo.city || customerInfo.clinic_city}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assign */}
      <div className="p-4 border-b border-zinc-100">
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Tildelt til</p>
        <div className="relative">
          <button
            onClick={() => setShowAssignMenu(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:border-zinc-300 transition-colors"
          >
            <div className="flex items-center gap-2">
              <UserCircle size={16} className="text-zinc-400" />
              <span className="text-zinc-700">{conversation.assigned_to || 'Ikke tildelt'}</span>
            </div>
            <CaretDown size={14} className="text-zinc-400" />
          </button>
          {showAssignMenu && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-10">
              <button onClick={() => { onAssign(''); setShowAssignMenu(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-500 hover:bg-zinc-50">
                Ingen
              </button>
              {agents.map(a => (
                <button key={a.id} onClick={() => { onAssign(a.name); setShowAssignMenu(false); }} className="w-full text-left px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-2">
                  <span>{a.avatar}</span> {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Labels */}
      <div className="p-4 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Labels</p>
          <button onClick={() => setShowLabelMenu(v => !v)} className="text-blue-600 hover:text-blue-700">
            <Plus size={15} />
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {currentLabels.length === 0 && <p className="text-xs text-zinc-400">Ingen labels</p>}
          {currentLabels.map(l => (
            <button key={l} onClick={() => toggleLabel(l)} className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${LABEL_COLORS[l] || 'bg-zinc-100 text-zinc-600'}`}>
              {l} <X size={10} />
            </button>
          ))}
        </div>
        {showLabelMenu && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {ALL_LABELS.filter(l => !currentLabels.includes(l)).map(l => (
              <button key={l} onClick={() => { toggleLabel(l); }} className={`text-xs px-2 py-0.5 rounded-full border border-dashed ${LABEL_COLORS[l] || 'bg-zinc-100 text-zinc-600'} opacity-70 hover:opacity-100`}>
                + {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Conversation info */}
      <div className="p-4">
        {conversation.attention_reason && (
          <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-700 mb-1">⚠ Kræver opfølgning</p>
            <p className="text-xs text-amber-800 leading-snug">{conversation.attention_reason}</p>
            {conversation.attention_at && (
              <p className="text-[10px] text-amber-600 mt-1">
                {new Date(conversation.attention_at).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        )}
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">Info</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Status</span>
            <span className={`capitalize font-medium ${conversation.status === 'active' ? 'text-emerald-600' : conversation.status === 'resolved' ? 'text-zinc-500' : 'text-amber-600'}`}>
              {conversation.status}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Beskeder</span>
            <span className="font-medium text-zinc-700">{conversation.message_count}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-500">Oprettet</span>
            <span className="font-medium text-zinc-700 text-xs">
              {new Date(conversation.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Conversations() {
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [agents, setAgents] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const selectedConvRef = useRef(null);

  useEffect(() => { selectedConvRef.current = selectedConversation; }, [selectedConversation]);

  useEffect(() => {
    fetchConversations();
    axios.get(`${API}/agents`).then(r => setAgents(r.data)).catch(() => {});

    let es;
    let reconnectTimer;
    const connect = () => {
      es = new EventSource(`${API}/events`);
      es.addEventListener('conversation', (e) => {
        fetchConversations();
        if (selectedConvRef.current?.id === e.data) fetchMessages(e.data);
      });
      es.addEventListener('message', (e) => {
        fetchConversations();
        if (selectedConvRef.current?.id === e.data) fetchMessages(e.data);
      });
      es.onerror = () => { es.close(); reconnectTimer = setTimeout(connect, 3000); };
    };
    connect();
    return () => { clearTimeout(reconnectTimer); es?.close(); };
  }, []);

  const fetchConversations = async () => {
    try {
      const r = await axios.get(`${API}/conversations`);
      const sorted = (r.data || []).sort((a, b) => new Date(b.last_message_time || 0) - new Date(a.last_message_time || 0));
      setConversations(sorted);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMessages = async (id) => {
    try {
      const r = await axios.get(`${API}/conversations/${id}`);
      const sorted = (r.data.messages || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(sorted);
      // Also update selected conv with latest data (labels, assigned_to etc)
      setSelectedConversation(prev => prev?.id === id ? { ...prev, ...r.data.conversation } : prev);
    } catch (e) { console.error(e); }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.id);
  };

  const handleSendMessage = async (message) => {
    if (!selectedConversation) return;
    try {
      await axios.post(`${API}/conversations/${selectedConversation.id}/reply`, null, { params: { message } });
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (e) { console.error(e); }
  };

  const handleMarkResolved = async () => {
    if (!selectedConversation) return;
    await axios.patch(`${API}/conversations/${selectedConversation.id}/status?status=resolved`);
    setSelectedConversation(prev => ({ ...prev, status: 'resolved' }));
    fetchConversations();
  };

  const handleReopen = async () => {
    if (!selectedConversation) return;
    await axios.patch(`${API}/conversations/${selectedConversation.id}/status?status=active`);
    setSelectedConversation(prev => ({ ...prev, status: 'active' }));
    fetchConversations();
  };

  const handleBotPauseToggle = async (paused) => {
    if (!selectedConversation) return;
    await axios.patch(`${API}/conversations/${selectedConversation.id}/bot-pause`, null, { params: { paused } });
    setSelectedConversation(prev => ({ ...prev, bot_paused: paused }));
    fetchConversations();
  };

  const handleAssign = async (agentName) => {
    if (!selectedConversation) return;
    await axios.patch(`${API}/conversations/${selectedConversation.id}/assign`, null, { params: { agent: agentName } });
    setSelectedConversation(prev => ({ ...prev, assigned_to: agentName }));
    fetchConversations();
  };

  const handleLabelChange = async (labels) => {
    if (!selectedConversation) return;
    await axios.patch(`${API}/conversations/${selectedConversation.id}/labels`, labels);
    setSelectedConversation(prev => ({ ...prev, labels }));
    fetchConversations();
  };

  const filteredConversations = conversations.filter(conv => {
    if (conv.status === 'merged') return false;
    if (filter !== 'all' && conv.platform !== filter) return false;
    if (searchQuery) {
      const s = searchQuery.toLowerCase();
      return conv.user_name?.toLowerCase().includes(s) || conv.user_id.toLowerCase().includes(s) || conv.last_message?.toLowerCase().includes(s);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="conversations-page">
      {showMergeModal && (
        <MergeModal
          onClose={() => setShowMergeModal(false)}
          onMerged={() => { fetchConversations(); }}
        />
      )}
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200 bg-white flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-2xl font-heading font-bold text-zinc-900">{t('conversations')}</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMergeModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
              title="Flet dublet-samtaler"
            >
              <GitMerge size={15} /> Flet samtaler
            </button>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              {conversations.filter(c => c.status === 'active').length} aktive
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t('search')} className="pl-9 h-9 text-sm" />
          </div>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="h-9">
              <TabsTrigger value="all" className="text-xs px-3">Alle</TabsTrigger>
              <TabsTrigger value="messenger" className="text-xs px-3">
                <MessengerLogo size={14} weight="fill" className="mr-1" /> Messenger
              </TabsTrigger>
              <TabsTrigger value="instagram" className="text-xs px-3">
                <InstagramLogo size={14} weight="fill" className="mr-1" /> Instagram
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="text-xs px-3">
                <WhatsAppIcon size={14} className="mr-1 text-[#25D366]" /> WhatsApp
              </TabsTrigger>
              <TabsTrigger value="webchat" className="text-xs px-3">
                <WebchatIcon size={14} className="mr-1 text-sky-500" /> Webchat
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* 3-column layout */}
      <div className="flex-1 grid overflow-hidden" style={{ gridTemplateColumns: '360px 1fr 260px' }}>
        {/* Conversation list */}
        <div className="border-r border-zinc-200 bg-white overflow-hidden">
          <ConversationList
            conversations={filteredConversations}
            selectedId={selectedConversation?.id}
            onSelect={handleSelectConversation}
            filter={filter}
          />
        </div>

        {/* Chat thread */}
        <div className="overflow-hidden">
          <ChatThread
            conversation={selectedConversation}
            messages={messages}
            onSendMessage={handleSendMessage}
            onMarkResolved={handleMarkResolved}
            onReopen={handleReopen}
            onBotPauseToggle={handleBotPauseToggle}
            onRefreshMessages={() => selectedConversation && fetchMessages(selectedConversation.id)}
          />
        </div>

        {/* Contact panel */}
        <ContactPanel
          conversation={selectedConversation}
          agents={agents}
          onAssign={handleAssign}
          onLabelChange={handleLabelChange}
        />
      </div>
    </div>
  );
}
